import React, { useCallback, useMemo, useState } from 'react';
import { BookOpen, ChevronDown, Database, LogOut, Megaphone, MessageCircle, ScrollText, Settings, Users } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/components/AuthProvider';
import { useNavigation } from '@/contexts/NavigationContext';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useSidebar } from '@/components/ui/sidebar';
import { isSubscriptionExpiredOrCancelled } from '@/lib/subscription-utils';
import { isSectionGranted } from '@/lib/menuPermissions.core';
import { WORKSPACE_BUSINESS_ACCOUNTS_URL } from '@/lib/workspaces/manageWorkspaces';
import { cn } from '@/lib/utils';
import { useEffectiveScopeId } from '@/hooks/useEffectiveScopeId';

type AccountLink = {
  label: string;
  url: string;
  featureKey: string | null;
  sectionId?: string;
};

export const ACCOUNT_SETTINGS_URL = '/home/account-settings';
export const ACCOUNT_BILLING_URL = `${ACCOUNT_SETTINGS_URL}?section=billing&interval=monthly`;
export const ACCOUNT_PLANS_URL = `${ACCOUNT_SETTINGS_URL}?section=plans&interval=monthly`;
export const ACCOUNT_BUSINESS_ACCOUNTS_URL = WORKSPACE_BUSINESS_ACCOUNTS_URL;
export const ACCOUNT_UPDATES_URL = '/home/updates';
export const ACCOUNT_GETTING_STARTED_URL = '/home/how-to-use';

const TOP_LINKS: AccountLink[] = [
  { label: 'Team management', url: '/home/team-management', featureKey: 'team_management' },
  { label: 'Team Chat', url: '/home/team-chat', featureKey: null },
  { label: 'Audit log', url: '/home/audit-log', featureKey: null },
  { label: 'Data Store', url: '/home/data-stores', featureKey: 'data_store', sectionId: 'data-store' },
];

const TOP_LINK_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  '/home/team-management': Users,
  '/home/team-chat': MessageCircle,
  '/home/audit-log': ScrollText,
  '/home/data-stores': Database,
};

const ACCOUNT_MENU_ROLE_RESTRICTIONS: Record<string, string[]> = {
  '/home/audit-log': ['ADMIN'],
  '/home/team-management': ['ADMIN'],
};

const ACCOUNT_MENU_URLS_BY_ROLE: Record<string, string[]> = {
  ADMIN: [
    ACCOUNT_SETTINGS_URL,
    '/home/team-management',
    '/home/audit-log',
    '/home/team-chat',
    '/home/data-stores',
  ],
  USER: [ACCOUNT_SETTINGS_URL, '/home/team-management', '/home/team-chat'],
  AGENT: [ACCOUNT_SETTINGS_URL, '/home/team-chat'],
  RIDER: [ACCOUNT_SETTINGS_URL],
};

function userInitials(fullName: string | undefined, email: string | undefined): string {
  if (fullName?.trim()) {
    return fullName
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email?.[0]?.toUpperCase() || 'U';
}

function accountRoleLabel(
  user:
    | {
        role?: string | null;
        is_super_admin?: boolean;
        is_agency?: boolean;
        is_organization_owner?: boolean;
      }
    | null
    | undefined,
): string {
  if (!user) return '';
  if (user.is_super_admin) return 'Super Admin';
  if (user.is_agency) return 'Agency';
  if (user.is_organization_owner) return 'Owner';
  const role = (user.role || '').toString().trim().toUpperCase();
  switch (role) {
    case 'ADMIN':
      return 'Admin';
    case 'USER':
      return 'User';
    case 'AGENT':
      return 'Agent';
    case 'RIDER':
      return 'Rider';
    default:
      return role
        ? role
            .toLowerCase()
            .split(/[^a-z0-9]+/i)
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : 'User';
  }
}

const menuItemClass =
  'flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg px-2 font-medium text-[#344054] hover:bg-[#F9FAFB]';

function useAccountMenuCanOpen() {
  const { user } = useAuth();
  const { data: entitlements } = useOrganizationEntitlements();
  const {
    data: currentSubscription,
    isLoading: isLoadingSubscription,
    isError: isSubscriptionError,
  } = useCurrentSubscription();

  const account = (user ?? {}) as {
    is_super_admin?: boolean;
    is_agency?: boolean;
    is_organization_owner?: boolean;
    menu_permissions?: string[] | null;
  };

  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!account.is_super_admin;
  const isAgency = !!account.is_agency;
  const isOwner = !!account.is_organization_owner;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin || isOwner;
  const currentRole = isAdmin
    ? 'ADMIN'
    : roleRaw === 'AGENT'
      ? 'AGENT'
      : roleRaw === 'RIDER'
        ? 'RIDER'
        : 'USER';

  const userMenuPermissions: string[] | null | undefined = account.menu_permissions;
  const hasCustomPermissions = !isSuperAdmin && Array.isArray(userMenuPermissions);

  const showsWorkspaceModules = !isSuperAdmin && !(isAdmin && isAgency);

  const isSubscriptionExpired =
    !isLoadingSubscription &&
    !isSubscriptionError &&
    (!currentSubscription || isSubscriptionExpiredOrCancelled(currentSubscription));

  const baseFeatures = entitlements?.entitlements?.features || {};

  const isFeatureEnabled = useCallback(
    (featureKey: string | null): boolean => {
      if (!featureKey) return true;
      if (featureKey === 'billing') return true;
      return baseFeatures[featureKey] === true;
    },
    [baseFeatures],
  );

  const urlsForRole = ACCOUNT_MENU_URLS_BY_ROLE[currentRole] ?? ACCOUNT_MENU_URLS_BY_ROLE.USER;

  const canOpen = useCallback(
    (url: string, featureKey: string | null, sectionId?: string) => {
      const path = url.split('?')[0];
      if (sectionId && !showsWorkspaceModules) return false;
      if (isSubscriptionExpired) return isFeatureEnabled(featureKey);
      const restricted = ACCOUNT_MENU_ROLE_RESTRICTIONS[path];
      const roleOk = !restricted || restricted.includes(currentRole);
      if (sectionId && hasCustomPermissions) {
        return (
          isSectionGranted(sectionId, userMenuPermissions as string[]) &&
          isFeatureEnabled(featureKey) &&
          roleOk
        );
      }
      return urlsForRole.includes(path) && isFeatureEnabled(featureKey) && roleOk;
    },
    [
      isSubscriptionExpired,
      isFeatureEnabled,
      urlsForRole,
      currentRole,
      hasCustomPermissions,
      userMenuPermissions,
      showsWorkspaceModules,
    ],
  );

  const showWhatsNew = currentRole !== 'RIDER';

  const showGettingStarted = currentRole === 'ADMIN';

  return { canOpen, showWhatsNew, showGettingStarted };
}

export function SidebarAccountFooter({ compact = false }: {
  compact?: boolean;
} = {}) {
  const { user, signOut } = useAuth();
  const { interceptedNavigate } = useNavigation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { data: accounts } = useBusinessAccounts();
  const selectedAccountId = useEffectiveScopeId();
  const { canOpen, showWhatsNew, showGettingStarted } = useAccountMenuCanOpen();
  const [open, setOpen] = useState(false);

  const selectedAccount = useMemo(
    () => accounts?.find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId],
  );

  const topLinks = TOP_LINKS.filter((item) =>
    canOpen(item.url, item.featureKey, item.sectionId),
  ).filter(
    (item) =>
      !(item.url === '/home/team-chat' && canOpen('/home/team-management', 'team_management')),
  );
  const showSettings = canOpen(ACCOUNT_SETTINGS_URL, null);

  const displayName =
    user?.full_name?.trim() || user?.email || user?.phone?.trim() || 'User';
  const initials = userInitials(user?.full_name, user?.email);
  const roleLabel = accountRoleLabel(user);
  const displayPhone =
    selectedAccount?.phone_number?.trim() || user?.phone?.trim() || '';

  const closeAndNavigate = (url: string) => {
    setOpen(false);
    interceptedNavigate(url);
    if (isMobile) setOpenMobile(false);
  };

  const handleSignOut = async () => {
    setOpen(false);
    await signOut?.();
    interceptedNavigate('/auth');
  };

  const menu = (
    <>
      {user && (
        <div
          data-testid="account-menu-identity"
          className="flex items-center gap-2.5 border-b border-[#EAECF0] px-3 py-3"
        >
          <Avatar className="h-9 w-9 shrink-0 !rounded-full">
            <AvatarImage src={user?.avatar_url} alt={displayName} />
            <AvatarFallback className="text-[13px] font-semibold !rounded-full bg-[#1D2939] text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div
              data-testid="account-menu-username"
              title={displayName}
              className="truncate text-[14px] font-semibold text-[#101828]"
            >
              {displayName}
            </div>
            {roleLabel ? (
              <div
                data-testid="account-menu-role"
                className="truncate text-[12px] text-[#667085]"
              >
                {roleLabel}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {(showSettings || topLinks.length > 0 || showGettingStarted || showWhatsNew) && (
        <div className="px-2 py-2">
          {showSettings && (
            <button
              type="button"
              data-testid="sidebar-account-settings"
              onClick={() => closeAndNavigate(ACCOUNT_SETTINGS_URL)}
              className={menuItemClass}
            >
              <Settings className="h-4 w-4 text-[#667085]" />
              <span className="flex-1 text-left">Account settings</span>
            </button>
          )}
          {topLinks.map((item) => {
            const LinkIcon = TOP_LINK_ICONS[item.url] ?? Users;
            return (
              <button
                type="button"
                key={item.url}
                data-testid={item.url === '/home/audit-log' ? 'sidebar-audit-log' : undefined}
                onClick={() => closeAndNavigate(item.url)}
                className={menuItemClass}
              >
                <LinkIcon className="h-4 w-4 text-[#667085]" />
                {item.label}
              </button>
            );
          })}
          {showGettingStarted && (
            <button
              type="button"
              data-testid="user-menu-getting-started"
              onClick={() => closeAndNavigate(ACCOUNT_GETTING_STARTED_URL)}
              className={menuItemClass}
            >
              <BookOpen className="h-4 w-4 text-[#667085]" />
              <span className="flex-1 text-left">Guide</span>
            </button>
          )}
          {showWhatsNew && (
            <button
              type="button"
              data-testid="user-menu-updates"
              onClick={() => closeAndNavigate(ACCOUNT_UPDATES_URL)}
              className={menuItemClass}
            >
              <Megaphone className="h-4 w-4 text-[#667085]" />
              <span className="flex-1 text-left">Updates</span>
            </button>
          )}
        </div>
      )}

      <div className="border-t border-[#EAECF0] px-2 py-2">
        <button
          type="button"
          data-testid="user-menu-logout"
          onClick={() => {
            void handleSignOut();
          }}
          className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg px-2 font-medium text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="sidebar-account-footer" className="flex-shrink-0 min-w-0">
      <HoverCard
        open={open}
        onOpenChange={setOpen}
        openDelay={80}
        closeDelay={220}
      >
        <HoverCardTrigger asChild>
          <button
            type="button"
            data-testid="user-menu-trigger"
            aria-label={`${displayName} account menu`}
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className={cn(
              'flex items-center rounded-xl text-left transition-colors hover:bg-[#F9FAFB] outline-none',
              compact
                ? 'h-9 gap-1 px-0.5'
                : 'gap-2 sm:gap-2.5 px-1.5 py-1 sm:px-2 sm:py-1.5'
            )}
          >
            <Avatar className={cn(
              'shrink-0 !rounded-full',
              compact ? 'h-8 w-8' : 'h-8 w-8 sm:h-9 sm:w-9'
            )}>
              <AvatarImage src={user?.avatar_url} alt={displayName} />
              <AvatarFallback className="text-[12px] sm:text-[13px] font-semibold !rounded-full bg-[#1D2939] text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            {!compact && (
              <div className="hidden sm:block min-w-0 max-w-[100px] md:max-w-[140px] lg:max-w-[180px]">
                <div className="truncate text-[14px] font-semibold text-[#101828]">{displayName}</div>
                {displayPhone ? (
                  <div className="truncate text-[13px] text-[#667085]">{displayPhone}</div>
                ) : null}
              </div>
            )}
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-[#98A2B3] transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="w-[280px] overflow-visible rounded-2xl border border-[#EAECF0] bg-white p-0 text-[14px] shadow-[0_12px_32px_rgba(16,24,40,0.12)] z-[80]"
        >
          {menu}
        </HoverCardContent>
      </HoverCard>
    </div>
  );
}
